import { z } from "zod";

import {
    FilterMode,
    FilterValueWithMode,
    hasException,
    isStringMode,
} from "../filterValueWithMode";

const EMPTY_VALUE = "null";

export const encodedArrayCodec = z.codec(
    z.string().nullable(),
    z.array(z.any()).nullable(), // eslint-disable-line zod/no-any-schema
    {
        decode: (string) => {
            if (string === EMPTY_VALUE || string === null) {
                return null;
            }
            try {
                return JSON.parse(string);
            } catch (error) {
                console.error("Error parsing JSON:", error);
                return null;
            }
        },
        encode: (array) => {
            if (array === null || array.length === 0) {
                return EMPTY_VALUE;
            }
            try {
                return JSON.stringify(array);
            } catch (error) {
                console.error("Error stringifying JSON:", error);
                return EMPTY_VALUE;
            }
        },
    }
);

export const numberRangeCodec = z.codec(
    z.string(),
    z.tuple([z.number().nullable(), z.number().nullable()]).nullable(),
    {
        encode: (value) => {
            if (!value) {
                return EMPTY_VALUE;
            }
            return value
                .map((n) => (n === null ? EMPTY_VALUE : n.toString()))
                .join(",");
        },
        decode: (encString) => {
            if (encString === EMPTY_VALUE) {
                return null;
            }
            const parts = encString
                .split(",")
                .map((s) => (s === EMPTY_VALUE ? null : Number(s))) as [
                number | null,
                number | null,
            ];
            return parts;
        },
    }
);

export const numberCodec = z.codec(z.string(), z.number(), {
    encode: (value) => value.toString(),
    decode: (encString) => Number(encString),
});
export const booleanCodec = z.codec(z.enum(["true", "false"]), z.boolean(), {
    encode: (value) => (value ? "true" : "false"),
    decode: (encString) => encString === "true",
});
export const nullCodec = z.codec(z.literal("null"), z.null(), {
    encode: () => "null" as const,
    decode: () => null,
});
export const dateCodec = z.codec(z.iso.date(), z.date(), {
    encode: (value) => value.toISOString().split("T")[0],
    decode: (encString) => new Date(encString),
});

export const dateRangeCodec = z.codec(
    z.string(),
    z.tuple([z.iso.date().nullable(), z.iso.date().nullable()]).nullable(),
    {
        encode: (value) => {
            if (!value) {
                return EMPTY_VALUE;
            }
            const mapped = value.map((date) => {
                const dateOnly = date ?? EMPTY_VALUE;
                return dateOnly;
            });
            return mapped.join(",");
        },
        decode: (encString) => {
            if (encString === EMPTY_VALUE) {
                return null;
            }

            const decodedDates = encString.split(",").map((dateString) => {
                if (dateString === EMPTY_VALUE) {
                    return null;
                }
                try {
                    const parsedDate = new Date(dateString)
                        .toISOString()
                        .split("T")[0];

                    return parsedDate;
                } catch (error) {
                    console.error(`Error parsing date: ${dateString}`, error);
                    return undefined;
                }
            }) as [string, string];

            return decodedDates;
        },
    }
);

const MODE_VALUE_DELIMITER = ".";

const MODE_TO_PREFIX: Record<FilterMode, string> = {
    is: "is",
    isNot: "not",
    contains: "con",
    notContains: "ncon",
};

const PREFIX_TO_MODE: Record<string, FilterMode> = {
    is: "is",
    not: "isNot",
    con: "contains",
    ncon: "notContains",
};

// A filter with an exception gets this suffix on its mode prefix, and its
// payload is a JSON array of the main and exception values.
const EXCEPTION_PREFIX_SUFFIX = "x";

/**
 * Wraps an existing codec to support FilterValueWithMode structure.
 * The mode is encoded as a suffix to the column ID: "columnId-mode.value"
 * This codec handles the "mode.value" portion, expecting the mode to be prepended.
 *
 * String-mode values bypass `baseCodec`: they are a plain substring,
 * JSON-encoded so any user text round-trips.
 *
 * A filter with an exception uses the mode prefix suffixed with "x" and a
 * JSON array `[main, exception]` as its payload, nesting each part's own
 * encoding. That keeps the URL readable but requires `baseCodec` to emit
 * JSON, which every set-mode array codec does.
 *
 * @example
 * ```ts
 * const encodedArrayWithModeCodec = withModeCodec(encodedArrayCodec);
 * // Encodes { value: ["a", "b"], mode: "isNot" } as 'not.["a","b"]'
 * // Encodes { value: "foo", mode: "contains" } as 'con."foo"'
 * // Encodes { value: ["a"], mode: "is", exception: ["b"] } as 'isx.[["a"],["b"]]'
 * // Full URL format: columnId-not.["a","b"]
 * ```
 */
export function withModeCodec<TEncoded extends string | null, TDecoded>(
    baseCodec: z.ZodCodec<z.ZodType<TEncoded>, z.ZodType<TDecoded>>
) {
    const encodePart = (value: TDecoded | null, mode: FilterMode): string => {
        if (isStringMode(mode)) {
            return JSON.stringify(typeof value === "string" ? value : null);
        }
        if (value === null) {
            return EMPTY_VALUE;
        }
        const encoded = baseCodec.encode(value);
        return typeof encoded === "string" ? encoded : EMPTY_VALUE;
    };

    const decodePart = (
        encodedValue: string,
        mode: FilterMode
    ): TDecoded | string | null | undefined => {
        if (isStringMode(mode)) {
            return decodeStringValue(encodedValue);
        }
        return baseCodec.decode(encodedValue);
    };

    return z.codec(
        z.string().nullable(),
        // eslint-disable-next-line zod/no-empty-custom-schema, zod/require-error-message
        z.custom<FilterValueWithMode<TDecoded> | null>(),
        {
            encode: (valueWithMode) => {
                if (!valueWithMode) {
                    return EMPTY_VALUE;
                }
                const { value, mode } = valueWithMode;
                const prefix = MODE_TO_PREFIX[mode];
                if (!hasException(valueWithMode)) {
                    return `${prefix}${MODE_VALUE_DELIMITER}${encodePart(value, mode)}`;
                }

                const parts = [value, valueWithMode.exception].map((part) =>
                    encodePart(part, mode)
                );
                if (!parts.every(isJson)) {
                    throw new Error(
                        "A filter exception requires a base codec that encodes to JSON"
                    );
                }
                return `${prefix}${EXCEPTION_PREFIX_SUFFIX}${MODE_VALUE_DELIMITER}[${parts.join(",")}]`;
            },
            decode: (encString, ctx) => {
                if (encString === null || encString === EMPTY_VALUE) {
                    return null;
                }

                const delimiterIndex = encString.indexOf(MODE_VALUE_DELIMITER);
                if (delimiterIndex === -1) {
                    ctx.issues.push({
                        code: "custom",
                        message: "Invalid filter format: missing mode",
                        input: encString,
                        path: [],
                    });
                    return z.NEVER;
                }

                const prefix = encString.slice(0, delimiterIndex);
                const withException = prefix.endsWith(EXCEPTION_PREFIX_SUFFIX);
                const modeStr = withException
                    ? prefix.slice(0, -EXCEPTION_PREFIX_SUFFIX.length)
                    : prefix;
                const mode = PREFIX_TO_MODE[modeStr];
                if (!mode) {
                    ctx.issues.push({
                        code: "custom",
                        message: `Invalid filter mode: ${prefix}`,
                        input: encString,
                        path: [],
                    });
                    return z.NEVER;
                }

                const payload = encString.slice(delimiterIndex + 1);
                const encodedParts = withException
                    ? splitExceptionPayload(payload)
                    : [payload];
                if (encodedParts === undefined) {
                    ctx.issues.push({
                        code: "custom",
                        message: `Invalid filter exception payload: ${payload}`,
                        input: encString,
                        path: [],
                    });
                    return z.NEVER;
                }

                const [value, exception] = encodedParts.map((part) =>
                    decodePart(part, mode)
                );
                if (
                    value === undefined ||
                    (withException && exception === undefined)
                ) {
                    ctx.issues.push({
                        code: "custom",
                        message: `Invalid ${mode} filter value: ${payload}`,
                        input: encString,
                        path: [],
                    });
                    return z.NEVER;
                }

                // String modes carry a substring where the static type has the
                // base codec's value; see the note on the test's `stringFilter`.
                const decoded = withException
                    ? { value, mode, exception }
                    : { value, mode };
                return decoded as FilterValueWithMode<TDecoded>;
            },
        }
    );
}

function isJson(text: string): boolean {
    try {
        JSON.parse(text);
        return true;
    } catch {
        return false;
    }
}

/**
 * Splits an exception payload `[main, exception]` back into the two parts'
 * own encodings; undefined if it isn't a two-element JSON array.
 */
function splitExceptionPayload(payload: string): [string, string] | undefined {
    try {
        const parsed: unknown = JSON.parse(payload);
        if (!Array.isArray(parsed) || parsed.length !== 2) {
            return undefined;
        }
        const [main, exception]: unknown[] = parsed;
        return [JSON.stringify(main), JSON.stringify(exception)];
    } catch {
        return undefined;
    }
}

/** Returns the decoded substring (or null for an empty filter); undefined if invalid. */
function decodeStringValue(encodedValue: string): string | null | undefined {
    try {
        const parsed: unknown = JSON.parse(encodedValue);
        if (parsed === null || typeof parsed === "string") {
            return parsed;
        }
        return undefined;
    } catch {
        return undefined;
    }
}
